import {
  Box,
  Button as ChakraButton,
  Center,
  Flex,
  Skeleton,
  SkeletonText,
  Stack,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Heading, StatusBadge, Table } from '~/components';
import { ROUTER_PATHS } from '~/constants';
import { useOccurrenceById } from '~/hooks/useOccurrences';
import { replaceParameters } from '~/utils/replaceParamaters';

export const OccurrenceDetail: React.VFC = () => {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useOccurrenceById(id);
  const history = useHistory();

  if (isLoading)
    return (
      <Box px="8" py="4">
        <Skeleton width="30%" height="20px" />
        <Flex
          flexDir="row"
          flexWrap="wrap"
          w="100%"
          py={9}
          gridGap="12"
          style={{
            position: 'relative',
          }}
        >
          <Stack maxW={400} w="100%" gridGap={4}>
            <SkeletonText mt="4" noOfLines={8} spacing="4" />
          </Stack>
          <Center
            position="relative"
            bottom="0"
            display={{ md: 'block', sm: 'none' }}
          />
          <Stack maxW={400} w="100%" gridGap={4}>
            <SkeletonText mt="4" noOfLines={8} spacing="4" />
          </Stack>
          <Center
            position="relative"
            bottom="0"
            display={{ md: 'block', sm: 'none' }}
          />
          <Stack maxW={400} w="100%" gridGap={4}>
            <SkeletonText mt="4" noOfLines={8} spacing="4" />
          </Stack>
        </Flex>
        <Flex flexFlow="column" maxW="1000px">
          <Stack>
            <Skeleton width="200px" height="20px" />
            <SkeletonText mt="4" noOfLines={8} spacing="4" />
          </Stack>
        </Flex>
        <Flex flexFlow="column" maxW="700px" mt="8">
          <Stack>
            <Skeleton width="200px" height="20px" />
            <SkeletonText mt="4" noOfLines={8} spacing="4" />
          </Stack>
        </Flex>
      </Box>
    );

  return (
    <Box px="8" py="4" overflowY="scroll" paddingBottom="8rem">
      <Heading hasBackButton>Chamado N° {data?.nu_chamado}</Heading>
      <Flex
        flexDir="row"
        flexWrap="wrap"
        w="100%"
        py={9}
        gridGap="12"
        style={{
          position: 'relative',
        }}
      >
        <Stack maxW={400} w="100%" gridGap={4}>
          <Heading fontSize="md" mb={4}>
            Informações Gerais
          </Heading>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              N° Chamado:
            </Text>
            <Text color="gray.500" fontSize="14px">
              {data?.nu_chamado}
            </Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              N° Série:
            </Text>
            <Text color="gray.500" fontSize="14px">
              {data?.nu_serie}
            </Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Data abertura:
            </Text>
            <Text color="gray.500" fontSize="14px">
              {data?.formattedOpeningDate || '-'}
            </Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Data encerramento:
            </Text>
            <Text color="gray.500" fontSize="14px">
              {data?.formattedClosingDate || '-'}
            </Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Problema Informado:
            </Text>
            <Text color="gray.500" fontSize="14px">
              {data?.ds_problemainformado || '-'}
            </Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Código Cliente:
            </Text>
            <Text color="gray.500" fontSize="14px">
              {data?.cod_cliente || '-'}
            </Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Razão Social / CNPJ:
            </Text>
            <Stack>
              <Text color="gray.500" fontSize="14px">
                {data?.nm_empresa || '-'}
              </Text>
              <Text color="gray.500" fontSize="14px">
                {data?.cnpj_empresa || '-'}
              </Text>
            </Stack>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Grupo Econômico:
            </Text>
            <Text color="gray.500" fontSize="14px">
              {data?.nm_grupoeconomico || '-'}
            </Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Status:
            </Text>
            <Text color="gray.500" fontSize="14px">
              <StatusBadge variant="">{data?.formattedStatus}</StatusBadge>
            </Text>
          </Flex>
        </Stack>
      </Flex>
      {data?.equipamentos && (
        <Flex flexFlow="column" maxW="600px" mt={4}>
          <Heading fontSize="md" mb={4}>
            Equipamentos
          </Heading>
          <Table type="secondary">
            <Thead>
              <Tr>
                <Th>Código</Th>
                <Th>Modelo</Th>
                <Th>Tipo</Th>
              </Tr>
            </Thead>
            <Tbody>
              {data?.equipamentos &&
                data?.equipamentos.map(equipment => (
                  <Tr key={equipment.id_equipamento}>
                    <Td>{equipment.cd_equipamento}</Td>
                    <Td>
                      {equipment?.modelo_equipamento?.nm_modeloequipamento ||
                        '-'}
                    </Td>
                    <Td>
                      {equipment?.tipo_equipamento?.nm_tipoequipamento || '-'}
                    </Td>
                  </Tr>
                ))}
            </Tbody>
          </Table>
        </Flex>
      )}
      {data?.contrato_equipamento && (
        <Flex flexFlow="column" maxW="600px" mt={8}>
          <Heading fontSize="md" mb={4}>
            Contratos
          </Heading>
          <Table type="secondary">
            <Thead>
              <Tr>
                <Th>Código</Th>
                <Th>Qtd. Equipamentos</Th>
                <Th>Detalhes</Th>
              </Tr>
            </Thead>
            <Tbody>
              {data?.contrato_equipamento &&
                data?.contrato_equipamento.map(contract => (
                  <Tr key={contract.id_contrato}>
                    <Td>{contract.id_contratoequipamento}</Td>
                    <Td>{contract?.qt_equipamento || '-'}</Td>
                    <Td>
                      <ChakraButton
                        variant="link"
                        fontWeight="normal"
                        color="#78797A"
                        onClick={() =>
                          history.push(
                            replaceParameters(
                              ROUTER_PATHS.PORTAL.CONCTRACT_DETAIL,
                              String(contract.id_contrato),
                            ),
                          )
                        }
                      >
                        Ver detalhes
                      </ChakraButton>
                    </Td>
                  </Tr>
                ))}
            </Tbody>
          </Table>
        </Flex>
      )}
    </Box>
  );
};
